const GENERIC_MESSAGE = 'Something went wrong. Please try again.'

// A JSON:API `detail` is meant to be a string, but not every server path sends
// one — `JsonAuthErrorHandler` sends `%{failure, reason}`. Rendering that object
// throws in any template that treats it as text (#5474) and reads as
// "[object Object]" in the rest, so fall back to the title, then generic copy.
function detailOf(error) {
  if (typeof error.detail === 'string') return error.detail
  return typeof error.title === 'string' ? error.title : GENERIC_MESSAGE
}

/**
 * Pull the first human-readable error message out of a `FormErrors` instance
 * (or any object with a `.messages` array). Returns a default if the errors
 * collection is empty or malformed. Used by modal action handlers that want
 * to surface a friendly inline message after a form submission fails.
 */
export function extractFirstMessage(formErrors, fallback = GENERIC_MESSAGE) {
  if (formErrors && formErrors.messages && formErrors.messages.length > 0) {
    // Skip non-string details so a malformed error payload can't surface as
    // "[object Object]" in the UI.
    const msg = formErrors.messages.find((m) => typeof m === 'string' && m.trim())
    if (msg) {
      return /[.!?]$/.test(msg) ? msg : `${msg}.`
    }
  }
  return fallback
}

export default class FormErrors {
  constructor(jsonApiErrors) {
    this.jsonApiErrors = jsonApiErrors || []
  }

  get messages() {
    return this.jsonApiErrors.map(detailOf)
  }

  get titles() {
    return this.jsonApiErrors.map((error) => error.title)
  }

  get genericMessages() {
    return this.generic.map(detailOf)
  }

  get hasAny() {
    return this.jsonApiErrors.length > 0
  }

  get generic() {
    return this.jsonApiErrors.filter((error) => typeof error.source !== 'object')
  }

  hasError(field) {
    return this.errorsByField(field).length > 0
  }

  // JSON:API `code` — a stable machine-readable identifier the server sets
  // alongside `detail` when the client needs to branch on *why* a request
  // failed rather than just display the message.
  hasCode(code) {
    return this.jsonApiErrors.some((error) => error.code === code)
  }

  messagesFor(field) {
    return this.errorsByField(field).map(detailOf)
  }

  errorsByField(field) {
    const dasherizedField = FormErrors.dasherize(field)

    return this.jsonApiErrors.filter((error) => {
      const source = error.source || { pointer: '' }

      return source.pointer === `/data/attributes/${dasherizedField}`
    })
  }

  static dasherize(field) {
    return field.replace(/([A-Z])/g, '-$1').toLowerCase()
  }
}

/**
 * Wrap an axios-style error's JSON:API `errors` in a `FormErrors`, falling back
 * to a generic message when the response carries none. Shared by the Vuex store
 * modules that surface API failures so the fallback copy stays in one place.
 */
export function resolveErrors(error) {
  const apiErrors = error?.response?.data?.errors
  return apiErrors ? new FormErrors(apiErrors) : new FormErrors([{ detail: GENERIC_MESSAGE }])
}
